/**
 * Training cost helpers — preserve explicit $0 (truthy checks turn 0 into null/undefined).
 */

/** Coerce DB/API cost fields to a finite number, or null when unset/invalid. Zero is kept. */
export function coerceOptionalTrainingCost(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Display/API threshold: costs below one cent are treated as free. */
export const FREE_TRAINING_COST_THRESHOLD = 0.01;

/** True when a cost is present and effectively free (< one cent). */
export function isEffectivelyFreeTrainingCost(value: unknown): boolean {
  const n = coerceOptionalTrainingCost(value);
  return n != null && n < FREE_TRAINING_COST_THRESHOLD;
}

/**
 * True when a title explicitly advertises no tuition.
 * Hyphenated words (Drug-Free, Hands-Free, Free-Flow) do not match.
 * "free of" / "free from" match only for "free of charge".
 */
export function titleImpliesFreeTraining(title: string | null | undefined): boolean {
  const t = title?.trim() ?? '';
  if (!t) return false;
  const re = /(?<![\w-])(free|no[\s-]cost|no[\s-]charge|tuition[\s-]free)(?![\w-])/gi;
  for (const match of t.matchAll(re)) {
    const word = match[1].toLowerCase();
    if (word !== 'free') return true;
    const after = t.slice((match.index ?? 0) + match[0].length);
    if (/^\s+of\s+charge\b/i.test(after)) return true;
    if (/^\s+(of|from)\b/i.test(after)) continue;
    return true;
  }
  return false;
}

export type EventPriceDisplayKind = 'free' | 'paid' | 'range' | 'unknown';

export type EventPriceDisplay = {
  kind: EventPriceDisplayKind;
  label: string;
  amount?: number;
  low?: number;
  high?: number;
};

function formatUsdTrainingPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

/**
 * One display rule for listing cards and event detail.
 * Unknown cost is "Price TBD" — never FREE and never $0.
 * A numeric cost wins over a title that mentions free.
 */
export function resolveEventPriceDisplay(input: {
  min?: unknown;
  max?: unknown;
  legacyPrice?: unknown;
  title?: string | null;
}): EventPriceDisplay {
  const min = coerceOptionalTrainingCost(input.min);
  const max = coerceOptionalTrainingCost(input.max);
  const legacy =
    min == null && max == null ? coerceOptionalTrainingCost(input.legacyPrice) : null;

  if (
    min != null &&
    max != null &&
    min !== max &&
    !(isEffectivelyFreeTrainingCost(min) && isEffectivelyFreeTrainingCost(max))
  ) {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return {
      kind: 'range',
      label: `${formatUsdTrainingPrice(low)} – ${formatUsdTrainingPrice(high)}`,
      low,
      high,
    };
  }

  const one = min ?? max ?? legacy;
  if (one != null) {
    if (isEffectivelyFreeTrainingCost(one)) {
      return { kind: 'free', label: 'FREE', amount: one };
    }
    return { kind: 'paid', label: formatUsdTrainingPrice(one), amount: one };
  }

  if (titleImpliesFreeTraining(input.title)) {
    return { kind: 'free', label: 'FREE' };
  }
  return { kind: 'unknown', label: 'Price TBD' };
}

/** Unknown prices sort after known prices. */
export function compareEventPriceForSort(a: number | null, b: number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

export type PublicEventFreeCostRow = {
  min_training_cost?: unknown;
  max_training_cost?: unknown;
  training_title?: string | null;
  title?: string | null;
};

/**
 * Matches public listing rows that are explicitly free.
 * Unset cost is not free. A paid min/max never matches, even when the title says free.
 */
export function eventRowMatchesFreeListingFilter(row: PublicEventFreeCostRow): boolean {
  const min = coerceOptionalTrainingCost(row.min_training_cost);
  if (min != null) return isEffectivelyFreeTrainingCost(min);
  const max = coerceOptionalTrainingCost(row.max_training_cost);
  if (max != null) return isEffectivelyFreeTrainingCost(max);
  return false;
}

/**
 * Prisma `events` where clause for GET /api/events?free=1.
 * Explicit cost under one cent only. Unset min and max are excluded.
 */
export function publicEventFreeCostWhere(): {
  OR: Array<Record<string, unknown>>;
} {
  return {
    OR: [
      { min_training_cost: { lt: FREE_TRAINING_COST_THRESHOLD } },
      {
        AND: [
          { min_training_cost: null },
          { max_training_cost: { lt: FREE_TRAINING_COST_THRESHOLD } },
        ],
      },
    ],
  };
}

/**
 * Resolve display price from min/max/legacy price fields without collapsing $0 to "missing".
 */
export function resolveTrainingDisplayCost(
  min: unknown,
  max: unknown,
  legacyPrice?: unknown,
): number | null {
  return (
    coerceOptionalTrainingCost(min) ??
    coerceOptionalTrainingCost(max) ??
    coerceOptionalTrainingCost(legacyPrice)
  );
}

/**
 * Parse a scraped/cost-field string into a number. Treats Free / no-charge phrases as 0.
 * Returns null when the text does not express a cost (including empty after stripping).
 */
export function parseTrainingCostFromText(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Match before digit stripping — "Free" otherwise becomes "" → NaN → null (Price TBD).
  if (/\b(free|no\s*(?:cost|charge|fee)|complimentary|at\s+no\s+cost)\b/i.test(s)) {
    return 0;
  }
  const num = parseFloat(s.replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(num)) return null;
  return num;
}

/**
 * Agency slugs for grant-funded crawler sources that publish tuition-free public courses.
 * When extractors omit cost, persist $0 so UI shows FREE instead of Price TBD.
 */
export const FREE_TUITION_CRAWLER_AGENCY_SLUGS = [
  'alerrt-texas-state-university',
  'texas-dre',
  'txsfst',
] as const;

/** Legacy ALERRT slug used in older rows/scripts; treat as free-tuition too. */
const FREE_TUITION_CRAWLER_AGENCY_SLUG_ALIASES = new Set([
  ...FREE_TUITION_CRAWLER_AGENCY_SLUGS,
  'alerrt',
]);

/** Target name / URL patterns for the same free-tuition crawler sources. */
const FREE_TUITION_CRAWLER_TARGET_NAMES = new Set(['ALERRT', 'TXSFST', 'Texas DRE']);
const FREE_TUITION_CRAWLER_URL_PATTERNS = ['alerrt.org', 'txsfst.org', 'texasdre.org'] as const;

/** True for ALERRT / TXSFST / Texas DRE crawler targets (grant-funded, no tuition). */
export function isFreeTuitionCrawlerSource(
  name?: string | null,
  url?: string | null,
): boolean {
  const n = name?.trim() ?? '';
  if (n && FREE_TUITION_CRAWLER_TARGET_NAMES.has(n)) return true;
  const u = (url ?? '').toLowerCase();
  return FREE_TUITION_CRAWLER_URL_PATTERNS.some((p) => u.includes(p));
}

/** True for platform agency slugs that host free-tuition crawled courses. */
export function isFreeTuitionCrawlerAgencySlug(slug?: string | null): boolean {
  const s = slug?.trim() ?? '';
  return s.length > 0 && FREE_TUITION_CRAWLER_AGENCY_SLUG_ALIASES.has(s);
}

export type ResolveCrawlerTrainingCostOptions = {
  /** When true and cost is unset, return 0 (known free tuition sources). */
  freeTuitionSource?: boolean;
  /** When cost is unset, titles containing "free" resolve to 0. */
  title?: string | null;
};

/**
 * Resolve scraped training cost for crawler_events / publish.
 * Accepts numbers, numeric strings, and Free/no-charge phrases; never collapses 0 to null.
 */
export function resolveCrawlerTrainingCost(
  raw: unknown,
  options?: ResolveCrawlerTrainingCostOptions,
): number | null {
  if (typeof raw === 'string') {
    const fromText = parseTrainingCostFromText(raw);
    if (fromText != null) return fromText;
  } else if (typeof raw === 'number' || typeof raw === 'bigint') {
    const fromNum = coerceOptionalTrainingCost(raw);
    if (fromNum != null) return fromNum;
  } else if (
    raw != null &&
    typeof raw === 'object' &&
    typeof (raw as { toNumber?: () => number }).toNumber === 'function'
  ) {
    // Prisma Decimal only — do not Number() arbitrary objects (e.g. Date → epoch ms).
    const fromNum = coerceOptionalTrainingCost((raw as { toNumber: () => number }).toNumber());
    if (fromNum != null) return fromNum;
  }
  if (options?.freeTuitionSource) return 0;
  const title = options?.title?.trim() ?? '';
  if (title && /\bfree\b/i.test(title)) return 0;
  return null;
}
